import { Controller } from "@hotwired/stimulus";

const STORAGE_KEY = "favorites";

// Favorite: a toggle that keeps a detail page in the reader's own browser. There
// are no accounts, so localStorage is the whole of it — nothing reaches the
// server. Each entry carries what a later favourites list needs to draw a card
// without asking the server (name, type, url, cover), newest first.
//
// The button ships `hidden` and is shown on connect only when storage works, so a
// reader without JavaScript or with storage blocked never meets a heart that
// forgets. The pressed look is `aria-pressed` (icon-btn styles it); the label
// stays put, as a toggle's should, and only the title says which way it goes.
//
//   <button hidden aria-pressed="false" data-controller="favorite"
//           data-action="favorite#toggle"
//           data-favorite-item-value='{"id":"City/3","name":"…","url":"…"}'>…</button>
export default class extends Controller {
  static values = {
    item: Object,
    addTitle: { type: String, default: "Favorilere ekle" },
    removeTitle: { type: String, default: "Favorilerden çıkar" },
  };

  connect() {
    if (this.read() === null) return;

    this.element.hidden = false;
    this.render();

    // Another tab added or removed the same page.
    this.onStorage = (event) => {
      if (event.key === STORAGE_KEY) this.render();
    };
    window.addEventListener("storage", this.onStorage);
  }

  disconnect() {
    window.removeEventListener("storage", this.onStorage);
  }

  toggle() {
    const favorites = this.read() ?? [];
    const id = this.itemValue.id;
    const rest = favorites.filter((favorite) => favorite.id !== id);

    this.write(
      rest.length === favorites.length
        ? [{ ...this.itemValue, savedAt: new Date().toISOString() }, ...rest]
        : rest,
    );
    this.render();
  }

  render() {
    const saved = (this.read() ?? []).some(
      (favorite) => favorite.id === this.itemValue.id,
    );

    this.element.setAttribute("aria-pressed", saved);
    this.element.title = saved ? this.removeTitleValue : this.addTitleValue;
  }

  // An array, or null when storage cannot be used at all (blocked, private
  // window). A corrupt value reads as an empty list rather than breaking the page.
  read() {
    try {
      const favorites = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
      return Array.isArray(favorites) ? favorites : [];
    } catch (error) {
      return error instanceof SyntaxError ? [] : null;
    }
  }

  write(favorites) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      // Quota or a storage revoked mid-visit: the button simply stays as it was.
    }
  }
}
